import { formatPhone, type ContactDetail, type ContactListItem, type HistoryEntry, type NoteItem, type RelatedContact } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RichText } from "./rich-text";
import { useUndoWindow } from "./undo";
import { useState } from "react";
import { Link } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { useSession } from "../session";
import { Alert, Button, Dialog, Empty, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField, cx, formatDateTime } from "./ui";

/** Notes on a job or contact, newest first (pinned on top). */
export function Notes({ path, queryKey, showJob }: { path: string; queryKey: unknown[]; showJob?: boolean }) {
  const { me, can } = useSession();
  const qc = useQueryClient();
  const notes = useQuery({ queryKey, queryFn: () => get<NoteItem[]>(path) });
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState<NoteItem | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey });

  const add = useMutation({
    mutationFn: () => post<NoteItem[]>(path, { body }),
    onSuccess: (list) => {
      qc.setQueryData(queryKey, list);
      setBody("");
    },
  });
  const pin = useMutation({ mutationFn: (n: NoteItem) => patch(`/api/notes/${n.id}`, { pinned: !n.pinned }), onSuccess: refresh });
  // Deleting is immediate; the note stays in place for a few seconds with Undo.
  const undo = useUndoWindow<NoteItem>();
  const remove = useMutation({ mutationFn: (n: NoteItem) => del(`/api/notes/${n.id}`), onSuccess: refresh, onError: (_e, n) => undo.release(n.id) });
  const restore = useMutation({ mutationFn: (n: NoteItem) => post(`/api/notes/${n.id}/restore`), onSuccess: refresh });

  return (
    <Panel>
      <PanelHeader title="Notes" />
      {can("notes.add") && (
        <form
          className="flex flex-col gap-2 border-b border-mist px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) add.mutate();
          }}
        >
          {add.error && <Alert>{errorMessage(add.error)}</Alert>}
          <label htmlFor={`${path}-note`} className="sr-only">
            New note
          </label>
          <textarea
            id={`${path}-note`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && body.trim()) add.mutate();
            }}
            placeholder="Add a note…"
            rows={2}
            className="w-full rounded-[var(--radius-control)] border border-line px-3 py-2 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
          />
          <div className="flex justify-end">
            <Button variant="primary" type="submit" busy={add.isPending} disabled={!body.trim()}>
              Add note
            </Button>
          </div>
        </form>
      )}
      {notes.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !undo.merge(notes.data ?? []).length ? (
        <Empty>No notes yet.</Empty>
      ) : (
        <ul>
          {undo.merge(notes.data ?? []).map((n, index) => undo.isHeld(n.id) ? (
            <li key={n.id} className="flex items-center gap-2 border-b border-mist px-5 py-3.5 text-sm text-muted last:border-0">
              Note deleted ·
              <button
                className="font-semibold text-brand hover:underline"
                onClick={() => {
                  undo.release(n.id);
                  restore.mutate(n);
                }}
              >
                Undo
              </button>
            </li>
          ) : (
            <li key={n.id} className={cx("border-b border-mist px-5 py-3.5 last:border-0", n.pinned && "bg-wait-wash/40")}>
              <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted">
                <span className="font-semibold text-ink">{n.author?.name ?? "Website form"}</span>
                <span>{formatDateTime(n.createdAt, me?.company.timezone)}</span>
                {n.editedAt && <span>(edited)</span>}
                {showJob && n.jobId && (
                  <Link to={`/jobs/${n.jobId}`} className="font-medium text-brand hover:underline">
                    Job #{n.jobNumber}
                  </Link>
                )}
                {n.pinned && <span className="font-semibold text-wait">Pinned</span>}
                <span className="ml-auto flex gap-1">
                  {can("notes.add") && (
                    <button className="rounded px-1.5 py-0.5 hover:bg-mist hover:text-ink" onClick={() => pin.mutate(n)}>
                      {n.pinned ? "Unpin" : "Pin"}
                    </button>
                  )}
                  {n.mine && (
                    <button className="rounded px-1.5 py-0.5 hover:bg-mist hover:text-ink" onClick={() => setEditing(n)}>
                      Edit
                    </button>
                  )}
                  {(n.mine || can("records.delete")) && (
                    <button
                      className="rounded px-1.5 py-0.5 hover:bg-danger-wash hover:text-danger"
                      onClick={() => {
                        undo.hold(n, index);
                        remove.mutate(n);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </span>
              </div>
              <RichText className="mt-1" text={n.body} />
            </li>
          ))}
        </ul>
      )}
      {editing && <EditNote note={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </Panel>
  );
}

function EditNote({ note, onClose, onSaved }: { note: NoteItem; onClose: () => void; onSaved: () => void }) {
  const [body, setBody] = useState(note.body);
  const save = useMutation({
    mutationFn: () => patch(`/api/notes/${note.id}`, { body }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog
      title="Edit note"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!body.trim()}>
            Save
          </Button>
        </>
      }
    >
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      <TextAreaField label="Note" value={body} onChange={(e) => setBody(e.target.value)} rows={6} autoFocus />
    </Dialog>
  );
}

/** Who changed what, from the activity log. */
export function History({ path, queryKey }: { path: string; queryKey: unknown[] }) {
  const { me } = useSession();
  const history = useQuery({ queryKey, queryFn: () => get<HistoryEntry[]>(path) });
  return (
    <Panel>
      <PanelHeader title="History" description="Every change, who made it, and when." />
      {history.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !history.data?.length ? (
        <Empty>Nothing yet.</Empty>
      ) : (
        <ol className="px-5 py-3">
          {history.data.map((h) => (
            <li key={h.id} className="flex gap-3 py-2">
              <span className="mt-2 size-2 shrink-0 rounded-full bg-brand/70" aria-hidden />
              <div>
                <div>{h.summary}</div>
                <div className="text-sm text-muted">
                  {h.actorName ?? "System"} · {formatDateTime(h.occurredAt, me?.company.timezone)}
                </div>
                <Changes details={h.details} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function Changes({ details }: { details: Record<string, unknown> }) {
  const changes = details.changes as Record<string, { from: unknown; to: unknown }> | undefined;
  if (!changes) return null;
  const show = (v: unknown) => (v === null || v === "" ? "(blank)" : String(v));
  const entries = Object.entries(changes).filter(([k]) => !k.endsWith("Id") && k !== "deductibleCents");
  if (!entries.length) return null;
  return (
    <ul className="mt-0.5 text-sm text-ink-soft">
      {entries.map(([k, v]) => (
        <li key={k}>
          <span className="text-muted">{k}:</span> {show(v.from)} → {show(v.to)}
        </li>
      ))}
    </ul>
  );
}

/** Related contacts (adjuster, spouse, property manager…) on a job or contact; on a job, its customer comes first. */
export function RelatedContacts({ contacts, basePath, invalidate, customer = null }: { contacts: RelatedContact[]; basePath: string; invalidate: unknown[]; customer?: RelatedContact | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const unlink = useMutation({
    mutationFn: (contactId: string) => del(`${basePath}/contacts/${contactId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: invalidate }),
  });
  return (
    <Panel>
      <PanelHeader
        title="Related contacts"
        action={
          can("customers.edit") && (
            <Button onClick={() => setAdding(true)} className="px-3 py-1.5">
              Add
            </Button>
          )
        }
      />
      {!contacts.length && !customer ? (
        <Empty>None yet. Add the adjuster, a spouse, a property manager…</Empty>
      ) : (
        <ul>
          {[...(customer ? [customer] : []), ...contacts].map((c) => (
            <li key={c.contactId} className="flex items-start justify-between gap-3 border-b border-mist px-5 py-3 last:border-0">
              <div className="min-w-0">
                <Link to={`/contacts/${c.contactId}`} className="font-semibold hover:text-brand">
                  {c.displayName}
                </Link>
                {c.role && <span className="ml-2 text-sm text-muted">{c.role}</span>}
                <div className="text-sm text-ink-soft">
                  {[c.typeName, c.company !== c.displayName && c.company, c.phone && <a key="p" href={`tel:${c.phone}`} className="hover:text-brand">{formatPhone(c.phone)}</a>, c.email && <a key="e" href={`mailto:${c.email}`} className="hover:text-brand">{c.email}</a>]
                    .filter(Boolean)
                    .map((x, i) => (
                      <span key={i}>
                        {i > 0 && " · "}
                        {x}
                      </span>
                    ))}
                </div>
              </div>
              {/* The job's customer is changed on the job, not removed here. */}
              {can("customers.edit") && c !== customer && (
                <button className="shrink-0 rounded px-1.5 py-0.5 text-sm text-muted hover:bg-mist hover:text-ink" onClick={() => unlink.mutate(c.contactId)} aria-label={`Remove ${c.displayName}`}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && <LinkContactDialog basePath={basePath} onClose={() => setAdding(false)} onDone={() => qc.invalidateQueries({ queryKey: invalidate })} />}
    </Panel>
  );
}

function LinkContactDialog({ basePath, onClose, onDone }: { basePath: string; onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<ContactListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ firstName: "", lastName: "", company: "", phone: "", email: "" });
  const [role, setRole] = useState(me?.lists.contactRoles[0] ?? "");
  // A new contact's type follows the role when there's a type by that name (Adjuster), otherwise Other.
  const types = me?.contactTypes ?? [];
  const typeFor = (r: string) => (types.find((t) => t.name.toLowerCase() === r.toLowerCase()) ?? types.find((t) => t.name === "Other") ?? types.at(-1))?.id ?? "";
  const [typeId, setTypeId] = useState(() => typeFor(role));
  const found = useQuery({
    queryKey: ["contacts", "pick", q],
    queryFn: () => get<{ items: ContactListItem[] }>(`/api/contacts?q=${encodeURIComponent(q)}&limit=8`),
    enabled: q.trim().length >= 2 && !creating,
  });
  const save = useMutation({
    mutationFn: async () => {
      const contactId = chosen?.id ?? (await post<ContactDetail>("/api/contacts", { ...draft, typeId })).id;
      await post(`${basePath}/contacts`, { contactId, role });
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog
      title="Add a related contact"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!chosen && !creating} onClick={() => save.mutate()}>
            Add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <SelectField
          label="Role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setTypeId(typeFor(e.target.value));
          }}
        >
          {me?.lists.contactRoles.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </SelectField>
        {chosen ? (
          <div className="flex items-center justify-between rounded-[var(--radius-control)] bg-mist px-3 py-2">
            <span>
              <span className="font-semibold">{chosen.displayName}</span> <span className="text-sm text-muted">{chosen.company}</span>
            </span>
            <button className="text-sm text-muted hover:text-ink" onClick={() => setChosen(null)}>
              Change
            </button>
          </div>
        ) : creating ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField className="sm:col-span-2" label="Contact type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </SelectField>
            <Field label="First name" value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} autoFocus />
            <Field label="Last name" value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
            <Field className="sm:col-span-2" label="Company" value={draft.company} onChange={(e) => setDraft({ ...draft, company: e.target.value })} hint="E.g. the insurance carrier" />
            <Field label="Phone" type="tel" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            <Field label="Email" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            <button className="text-left text-sm text-muted hover:text-ink sm:col-span-2" onClick={() => setCreating(false)}>
              ← Pick an existing contact instead
            </button>
          </div>
        ) : (
          <div>
            <Field label="Find a contact" placeholder="Name, company, phone…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <ul className="mt-2">
              {found.data?.items.map((c) => (
                <li key={c.id}>
                  <button className="w-full rounded px-2 py-1.5 text-left hover:bg-mist" onClick={() => setChosen(c)}>
                    <span className="font-medium">{c.displayName}</span> <span className="text-sm text-muted">{[c.typeName, c.company !== c.displayName && c.company, formatPhone(c.phone)].filter(Boolean).join(" · ")}</span>
                  </button>
                </li>
              ))}
            </ul>
            <button className="mt-2 text-sm font-semibold text-brand hover:underline" onClick={() => setCreating(true)}>
              + New contact
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
